<template>
  <div class="informes-page">
    <PageHeader title="Informes de conductores" subtitle="Enlaces para que cada conductor informe qué se trabajó en sus salidas">
      <template #actions>
        <Button icon="pi pi-plus" label="Nuevo enlace" @click="abrirCrear" />
      </template>
    </PageHeader>

    <div class="informes-content">
      <div v-if="loading" class="text-center py-5 text-500">Cargando…</div>

      <template v-else>
        <!-- Enlaces -->
        <EmptyState
          v-if="!tokens.length"
          icon="pi-check-square"
          title="Sin enlaces de informe"
          description="Crea uno y compártelo con los conductores para que informen sus salidas."
        >
          <template #action>
            <Button icon="pi pi-plus" label="Nuevo enlace" @click="abrirCrear" />
          </template>
        </EmptyState>

        <div v-else class="flex flex-column gap-3">
          <div v-for="t in tokens" :key="t.token" class="informe-card">
            <div class="flex align-items-start gap-3 flex-wrap">
              <div class="flex-1 min-w-0">
                <div class="text-lg font-semibold mb-2">{{ t.name }}</div>

                <div class="flex flex-wrap align-items-center gap-1 mb-2">
                  <i class="pi pi-users text-500 mr-1" />
                  <span v-if="!t.conductorIds.length" class="text-sm text-700">Todos los conductores</span>
                  <Tag v-for="n in conductorNames(t.conductorIds)" v-else :key="n" :value="n" severity="secondary" />
                </div>

                <div class="flex align-items-center gap-2 text-sm mb-2">
                  <i class="pi pi-calendar text-500" />
                  <span class="text-700">
                    {{ t.desde ? `Pide informes de salidas desde el ${fmtFecha(t.desde)}` : 'Pide informes de todas las salidas' }}
                  </span>
                </div>

                <div class="flex align-items-center gap-2 text-sm mb-2">
                  <i class="pi pi-clock text-500" />
                  <span :class="pendientesDe(t).length ? 'text-orange-600 font-semibold' : 'text-700'">
                    {{ pendientesDe(t).length
                      ? `${pendientesDe(t).length} ${pendientesDe(t).length === 1 ? 'salida pendiente' : 'salidas pendientes'} de informar`
                      : 'Sin salidas pendientes' }}
                  </span>
                </div>

                <div class="flex align-items-center gap-1 mt-2">
                  <span
                    class="text-xs text-400 font-mono overflow-hidden text-overflow-ellipsis white-space-nowrap flex-1"
                    style="max-width:340px"
                  >{{ getPublicUrl(t.token) }}</span>
                  <Button
                    :icon="copied === t.token ? 'pi pi-check' : 'pi pi-copy'"
                    size="small"
                    text
                    :severity="copied === t.token ? 'success' : 'secondary'"
                    v-tooltip="'Copiar enlace'"
                    @click="copyLink(t)"
                  />
                  <Button
                    icon="pi pi-external-link"
                    size="small"
                    text
                    severity="secondary"
                    v-tooltip="'Abrir enlace'"
                    @click="abrirEnlace(t)"
                  />
                </div>
              </div>

              <div class="flex flex-column gap-2 flex-shrink-0">
                <Button icon="pi pi-pencil" label="Editar" size="small" severity="secondary" outlined @click="abrirEditar(t)" />
                <Button
                  icon="pi pi-share-alt"
                  label="Metadata"
                  size="small"
                  severity="secondary"
                  outlined
                  v-tooltip="'Cómo se ve al compartirlo'"
                  @click="openMetadata(t)"
                />
                <Button
                  icon="pi pi-trash"
                  size="small"
                  severity="danger"
                  text
                  v-tooltip="'Eliminar enlace'"
                  @click="confirmDelete(t)"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Estado de informes -->
        <div class="informe-card mt-4">
          <div class="flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
            <div class="text-lg font-semibold">Estado de informes</div>
            <SelectButton
              v-model="filtroEstado"
              :options="FILTROS"
              optionLabel="label"
              optionValue="value"
              :allowEmpty="false"
            />
          </div>

          <div v-if="!filasFiltradas.length" class="text-center text-500 py-4">
            {{ filtroEstado === 'pendiente' ? 'No hay salidas pendientes de informar.' : 'No hay salidas para mostrar.' }}
          </div>

          <DataTable
            v-else
            :value="filasFiltradas"
            size="small"
            :paginator="filasFiltradas.length > 20"
            :rows="20"
            dataKey="id"
            responsiveLayout="stack"
            breakpoint="720px"
          >
            <Column header="Fecha" style="width:130px">
              <template #body="{ data }">
                <div class="font-medium">{{ fmtFecha(data.date) }}</div>
                <div v-if="data.hour" class="text-xs text-500">{{ data.hour }} hs</div>
              </template>
            </Column>
            <Column header="Conductor">
              <template #body="{ data }">{{ conductorName(data.conductor) }}</template>
            </Column>
            <Column header="Territorios">
              <template #body="{ data }">
                <div class="flex flex-wrap gap-1">
                  <template v-if="data.informe">
                    <Tag
                      v-for="t in data.informe.territorios"
                      :key="t.key"
                      :value="t.totalManzanas ? `${t.key} · ${(t.manzanas || []).length}/${t.totalManzanas}` : t.key"
                      :icon="t.trabajado ? 'pi pi-check' : 'pi pi-times'"
                      :severity="t.trabajado ? 'success' : 'secondary'"
                      v-tooltip="tooltipTerritorio(t)"
                    />
                  </template>
                  <Tag v-for="k in data.territorios" v-else :key="k" :value="k" severity="secondary" />
                </div>
              </template>
            </Column>
            <Column header="Notas">
              <template #body="{ data }">
                <span class="text-sm white-space-pre-line">{{ data.informe?.notas || '' }}</span>
              </template>
            </Column>
            <Column header="Estado" style="width:170px">
              <template #body="{ data }">
                <div class="flex align-items-center gap-2">
                  <Tag
                    :value="data.informe ? 'Informada' : 'Pendiente'"
                    :severity="data.informe ? 'success' : 'warning'"
                  />
                  <Button
                    v-if="data.informe"
                    icon="pi pi-replay"
                    size="small"
                    text
                    severity="secondary"
                    v-tooltip="'Reabrir: borra el informe y vuelve a quedar pendiente'"
                    @click="confirmReabrir(data)"
                  />
                </div>
              </template>
            </Column>
          </DataTable>
        </div>
      </template>
    </div>

    <!-- Dialog: crear / editar enlace -->
    <Dialog
      v-model:visible="showForm"
      :header="editando ? 'Editar enlace' : 'Nuevo enlace de informe'"
      modal
      style="width:420px;max-width:96vw"
    >
      <div class="flex flex-column gap-3 pt-2">
        <div class="flex flex-column gap-1">
          <label class="text-sm">Nombre del enlace</label>
          <InputText v-model="form.name" placeholder="Ej: Conductores de la semana" autofocus />
        </div>

        <div class="flex flex-column gap-1">
          <label class="text-sm">Conductores</label>
          <MultiSelect
            v-model="form.conductorIds"
            :options="conductores"
            optionLabel="name"
            optionValue="id"
            placeholder="Todos los conductores"
            display="chip"
            filter
            class="w-full"
          />
          <small class="text-500">Déjalo vacío para que aparezcan todos.</small>
        </div>

        <div class="flex flex-column gap-1">
          <label class="text-sm">Pedir informes de salidas desde</label>
          <Calendar v-model="form.desde" dateFormat="dd/mm/yy" showButtonBar class="w-full" placeholder="Todas las salidas" />
          <small class="text-500">Las salidas anteriores a esta fecha no se piden.</small>
        </div>

        <div class="flex gap-2 justify-content-end">
          <Button label="Cancelar" severity="secondary" @click="showForm = false" />
          <Button
            :label="editando ? 'Guardar' : 'Crear'"
            :disabled="!form.name.trim()"
            :loading="guardando"
            @click="guardar"
          />
        </div>
      </div>
    </Dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouterStore } from '@/store/router'
import { useInformeTokens } from '@/composables/useInformeTokens'
import { useApi } from '@/composables/useApi'
import { useConfirm } from 'primevue/useconfirm'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import MultiSelect from 'primevue/multiselect'
import Calendar from 'primevue/calendar'
import SelectButton from 'primevue/selectbutton'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Tag from 'primevue/tag'
import PageHeader from '@/components/ui/PageHeader.vue'
import EmptyState from '@/components/ui/EmptyState.vue'

const route = useRouterStore()
const api = useApi()
const confirm = useConfirm()
const { getPublicUrl, getTokens, createToken, updateToken, revokeToken, getInformes, reabrirInforme } = useInformeTokens()

const loading = ref(true)
const tokens = ref([])
const salidas = ref([])
const informes = ref([])

const conductores = ref([])
const conductorName = (id) => conductores.value.find((c) => c.id === id)?.name || '—'
const conductorNames = (ids) => ids.map(conductorName)

const salidaMs = (date) => {
  if (date == null || date === '') return null
  const ms = typeof date === 'number' ? date : /^\d+$/.test(String(date)) ? Number(date) : Date.parse(date)
  return Number.isFinite(ms) ? ms : null
}

const fmtFecha = (value) => {
  const ms = salidaMs(value)
  if (ms == null) return ''
  return new Date(ms).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

// Misma regla que el servidor (server/utils/informes.js → esInformable).
const informables = computed(() => {
  const now = Date.now()
  const porId = new Map(informes.value.map((i) => [String(i.salidaId), i]))
  return salidas.value
    .filter((s) => {
      const ms = salidaMs(s.date)
      return ms != null && ms <= now && s.conductor && (s.territorios || []).length
    })
    .map((s) => ({ ...s, ms: salidaMs(s.date), informe: porId.get(String(s.id)) || null }))
    .sort((a, b) => b.ms - a.ms)
})

const tooltipTerritorio = (t) => {
  if (!t.trabajado) return 'No se trabajó'
  if (!t.totalManzanas) return 'Se trabajó'
  return `Manzanas trabajadas: ${t.manzanas.join(', ')} (de ${t.totalManzanas} que faltaban)`
}

const cubiertaPor = (t, s) =>
  (!t.desde || s.ms >= t.desde) && (!t.conductorIds.length || t.conductorIds.includes(s.conductor))

const pendientesDe = (t) => informables.value.filter((s) => !s.informe && cubiertaPor(t, s))

// Sólo se listan salidas alcanzadas por algún enlace: las demás nunca se
// van a informar y sólo harían ruido.
const filas = computed(() => informables.value.filter((s) => s.informe || tokens.value.some((t) => cubiertaPor(t, s))))

const FILTROS = [
  { label: 'Pendientes', value: 'pendiente' },
  { label: 'Informadas', value: 'informada' },
  { label: 'Todas', value: 'todas' },
]
const filtroEstado = ref('pendiente')

const filasFiltradas = computed(() => {
  if (filtroEstado.value === 'pendiente') return filas.value.filter((s) => !s.informe)
  if (filtroEstado.value === 'informada') return filas.value.filter((s) => s.informe)
  return filas.value
})

const loadAll = async () => {
  loading.value = true
  const [tokensRes, salidasRes, informesRes, conductoresRes] = await Promise.all([
    getTokens(),
    api.get('/api/salidas'),
    getInformes(),
    api.get('/api/conductores'),
  ])
  conductores.value = conductoresRes.conductores || []
  tokens.value = tokensRes.tokens || []
  salidas.value = [...(salidasRes.salidas || []), ...(salidasRes.history || [])]
  informes.value = informesRes.informes || []
  loading.value = false
}

onMounted(loadAll)

// ── Crear / editar ────────────────────────────────────────────────────────────
const showForm = ref(false)
const editando = ref(null)
const guardando = ref(false)
const form = ref({ name: '', conductorIds: [], desde: null })

const hoy = () => {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d
}

const abrirCrear = () => {
  editando.value = null
  // Por defecto sólo se piden informes desde hoy, para no arrastrar salidas viejas.
  form.value = { name: '', conductorIds: [], desde: hoy() }
  showForm.value = true
}

const abrirEditar = (t) => {
  editando.value = t
  form.value = { name: t.name, conductorIds: [...t.conductorIds], desde: t.desde ? new Date(t.desde) : null }
  showForm.value = true
}

const guardar = async () => {
  if (!form.value.name.trim()) return
  guardando.value = true
  const desde = form.value.desde ? new Date(form.value.desde) : null
  desde?.setHours(0, 0, 0, 0)
  const payload = {
    name: form.value.name.trim(),
    conductorIds: form.value.conductorIds,
    desde: desde ? desde.getTime() : null,
  }
  const res = editando.value
    ? await updateToken(editando.value.token, payload)
    : await createToken(payload)
  guardando.value = false
  if (res.error || !res.token) return
  if (editando.value) {
    Object.assign(tokens.value.find((x) => x.token === res.token), res)
  } else {
    tokens.value.unshift(res)
  }
  showForm.value = false
}

// ── Eliminar / reabrir ────────────────────────────────────────────────────────
const confirmDelete = (t) => {
  confirm.require({
    message: `¿Eliminar el enlace "${t.name}"? Los conductores ya no podrán informar con él. Los informes ya enviados se conservan.`,
    header: 'Confirmar eliminación',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    acceptLabel: 'Eliminar',
    rejectLabel: 'Cancelar',
    accept: async () => {
      await revokeToken(t.token)
      tokens.value = tokens.value.filter((x) => x.token !== t.token)
    },
  })
}

const confirmReabrir = (s) => {
  confirm.require({
    message: `¿Reabrir el informe del ${fmtFecha(s.date)} de ${conductorName(s.conductor)}? Se borra lo informado (también del Informe S-13) y la salida vuelve a quedar pendiente.`,
    header: 'Reabrir informe',
    icon: 'pi pi-replay',
    acceptLabel: 'Reabrir',
    rejectLabel: 'Cancelar',
    accept: async () => {
      const res = await reabrirInforme(s.id)
      if (!res.error) informes.value = informes.value.filter((i) => String(i.salidaId) !== String(s.id))
    },
  })
}

// ── Compartir ─────────────────────────────────────────────────────────────────
const copied = ref(null)
const copyLink = (t) => {
  navigator.clipboard.writeText(getPublicUrl(t.token))
  copied.value = t.token
  setTimeout(() => { if (copied.value === t.token) copied.value = null }, 2000)
}

const abrirEnlace = (t) => window.open(getPublicUrl(t.token), '_blank')

const openMetadata = (t) => {
  route.page = 'metadataEnlaces'
  route.params = { type: 'informe', token: t.token }
}
</script>

<style scoped>
.informes-page {
  width: 100%;
  text-align: left;
}
.informes-content {
  padding: 0 var(--space-5) var(--space-5);
}
.informe-card {
  background: var(--bg-primary);
  border-radius: var(--radius-lg);
  padding: var(--space-4) var(--space-5);
  box-shadow: var(--shadow-1);
}
@media (max-width: 720px) {
  .informes-content {
    padding: 0 var(--space-3) var(--space-4);
  }
  .informe-card {
    padding: var(--space-3);
  }
}
</style>
